import { SuggestionData } from 'ckeditor5-premium-features'
import { TrackedChange } from 'models/changes'
import React, { RefObject, memo, useCallback } from 'react'
import { SuggestionCardWrapper } from '../SuggestionCardWrapper'

interface CustomSuggestionCardWrapperProps {
    visibleSuggestions: SuggestionData[]
    containerRef: RefObject<HTMLDivElement>
    containerHeight: number
    totalHeight: number
    heightsRef: React.MutableRefObject<number[]>
    suggestions: SuggestionData[]
    visibleRange: { start: number; end: number }
    setPositions: React.Dispatch<React.SetStateAction<number[]>>
    isNotDefaultSuggestionCardFilter: boolean
    positions: number[]
    trackChangesData: Record<string, TrackedChange>
    suggestionCardFilter: string
}

/**
 * CustomSuggestionCardWrapper component handles the rendering of suggestion cards
 * with virtualization and dynamic height measurement.
 * It uses a ref to measure the height of each card
 * and updates positions accordingly.
 * @param {CustomSuggestionCardWrapperProps} props - The properties for the component.
 * @return {JSX.Element} The rendered custom suggestion card wrapper.
 */
export const CustomSuggestionCardWrapper = memo(
    ({
        visibleSuggestions,
        containerRef,
        containerHeight,
        totalHeight,
        heightsRef,
        suggestions,
        visibleRange,
        setPositions,
        isNotDefaultSuggestionCardFilter,
        positions,
        trackChangesData,
        suggestionCardFilter,
    }: CustomSuggestionCardWrapperProps) => {
        // Efficiently measure and update positions only when needed
        const measureRef = useCallback(
            (el: HTMLDivElement | null, idx: number) => {
                if (el) {
                    const height = el.offsetHeight
                    if (heightsRef.current[idx] !== height) {
                        heightsRef.current[idx] = height
                        // Recalculate positions only if changed
                        const newPositions: number[] = []
                        let acc = 0
                        for (let i = 0; i < suggestions.length; i++) {
                            newPositions[i] = acc
                            acc += heightsRef.current[i] || 0
                        }
                        setPositions(newPositions)
                    }
                }
            },
            [suggestions.length, setPositions, heightsRef]
        )

        // Fallback: render cards in normal flow so heights can be measured
        if (
            isNotDefaultSuggestionCardFilter &&
            (positions.length !== suggestions.length ||
                positions.every((pos: number) => pos === 0))
        ) {
            return (
                <div
                    style={{
                        height: containerHeight,
                        position: 'relative',
                        overflowY: 'auto',
                    }}
                >
                    {suggestions.slice(0, 30).map((suggestion, idx) => {
                        return (
                            <div
                                key={suggestion.id}
                                ref={(el) => measureRef(el, idx)}
                                style={{
                                    width: '100%',
                                }}
                            >
                                <SuggestionCardWrapper
                                    suggestion={suggestion}
                                    trackChangesData={trackChangesData}
                                    index={idx}
                                    isNotDefaultSuggestionCardFilter={
                                        isNotDefaultSuggestionCardFilter
                                    }
                                    suggestionCardFilter={suggestionCardFilter}
                                />
                            </div>
                        )
                    })}
                </div>
            )
        }

        // Main render: absolutely position cards for virtualization
        return (
            <div
                ref={containerRef}
                style={{
                    height: containerHeight,
                    overflowY: 'auto',
                    position: 'relative',
                    willChange: 'transform',
                    background: 'inherit',
                }}
            >
                <div style={{ height: totalHeight, position: 'relative' }}>
                    {visibleSuggestions.map((suggestion, idx) => {
                        const actualIndex = visibleRange?.start + idx
                        return (
                            <div
                                key={suggestion.id}
                                ref={(el) => measureRef(el, actualIndex)}
                                style={{
                                    position: 'absolute',
                                    top: isNotDefaultSuggestionCardFilter
                                        ? positions[actualIndex] || 0
                                        : 0,
                                    left: 0,
                                    right: 0,
                                    width: '100%',
                                }}
                            >
                                <SuggestionCardWrapper
                                    suggestion={suggestion}
                                    trackChangesData={trackChangesData}
                                    index={actualIndex}
                                    isNotDefaultSuggestionCardFilter={
                                        isNotDefaultSuggestionCardFilter
                                    }
                                    suggestionCardFilter={suggestionCardFilter}
                                />
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }
)
